<script setup lang="ts">
import { publicContainerApi } from '@libero/api-client/public-container/api';
import { StorePublicTicketRequest } from '@libero/api-client/public-ticket/types';
import { Ticket } from '@libero/api-client/ticket/types';
import { useTenantStore } from '@libero/hooks/store/useTenantStore';
import { useFormContext } from '@libero/hooks/useForm';
import ThemeIcon from '@libero/organisms/ThemeIcon/ThemeIcon.vue';
import { Color } from '@libero/types/Color';
import Button from '@libero/ui-framework/Button/Button.vue';
import Cluster from '@libero/ui-framework/Cluster/Cluster.vue';
import List from '@libero/ui-framework/List/List.vue';
import ListItem from '@libero/ui-framework/List/ListItem.vue';
import Stack from '@libero/ui-framework/Stack/Stack.vue';
import Typography from '@libero/ui-framework/Typography/Typography.vue';
import { keepPreviousData, useQuery } from '@tanstack/vue-query';
import { uniq } from 'lodash';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

interface Props {
  ticket: Ticket;
}

defineProps<Props>();

const router = useRouter();
const { t } = useI18n();
const { tenant } = useTenantStore();

const form = useFormContext<StorePublicTicketRequest>();

const { data: containers } = useQuery({
  queryKey: ['containers', form.values.containers],
  queryFn: () => {
    if (!form.values.containers.length) return null;

    return publicContainerApi.index({
      filter: { id: form.values.containers.join(',') },
    });
  },
  placeholderData: keepPreviousData,
});

const theme = computed(() => tenant?.themes.find((theme) => theme.id === form.values.theme_id));

const gridOperators = computed(() =>
  containers.value && containers.value.length > 1
    ? uniq(containers.value?.map((container) => container.grid_operator)).join(', ')
    : undefined,
);
</script>

<template>
  <Stack :gap="1.5">
    <Stack>
      <Typography tag="h1" type="heading" size="xl" bold>
        {{ t('citizen-report.thanks-title') }}
      </Typography>

      <Typography size="lg">
        {{ t('citizen-report.thanks-description', { number: ticket.number }) }}
      </Typography>

      <Typography v-if="gridOperators" size="lg">
        {{ t('citizen-report.thanks-grid-operators-notice', { name: gridOperators }) }}
      </Typography>
    </Stack>

    <Stack :gap="1">
      <Typography tag="h2" type="heading" size="lg" bold>
        {{ t('citizen-report.your-report') }}
      </Typography>

      <Stack :gap="0.25">
        <Typography size="lg">
          <Typography size="lg" bold>
            {{ t('citizen-report.report-category') }}
          </Typography>

          {{ theme?.name }}
        </Typography>

        <Typography size="lg">
          <Typography size="lg" bold>
            {{ t('citizen-report.report-subject') }}
          </Typography>

          {{ ticket.ticket_subject?.name }}
        </Typography>

        <Typography v-if="form.values.explanation" size="lg">
          <Typography size="lg" bold>
            {{ t('citizen-report.report-explanation') }}
          </Typography>

          {{ form.values.explanation }}
        </Typography>
      </Stack>
    </Stack>

    <Stack :gap="0.25">
      <List v-for="container in containers" :key="container.id" size="lg">
        <ListItem>
          <Cluster :gap="0.75" alignItems="center">
            <ThemeIcon v-if="theme" :theme="theme.key" size="lg" :color="Color.gray500" />

            <Typography size="lg" bold>
              {{ container.name }}
            </Typography>
          </Cluster>
        </ListItem>
      </List>
    </Stack>

    <Cluster>
      <Button size="lg" :onClick="() => router.go(0)">
        {{ t('citizen-report.new-report') }}
      </Button>
    </Cluster>
  </Stack>
</template>
